import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Alert, Box, Typography } from '@mui/material';
import isWebGLSupported from '@ui-framework/helpers/isWebGLSupported';

const MapSupportCheckContext = createContext(null);

export function useMapSupportCheck() {
  const context = useContext(MapSupportCheckContext);

  if (!context) {
    throw new Error('useMapSupportCheck must be used within MapSupportCheck');
  }

  return context;
}

function MapSupportCheckUnsupported({ error }) {
  return (
    <Box p={2}>
      <Alert severity="warning">
        <Typography variant="subtitle1" gutterBottom>
          Map cannot be displayed
        </Typography>
        <Typography variant="body2">{error}</Typography>
        <ul>
          <li>Enable hardware acceleration in your browser settings.</li>
          <li>Update your browser to the latest version.</li>
          <li>Try Chrome, Edge, Firefox, or Safari.</li>
          <li>Disable graphics-related browser extensions.</li>
          <li>Restart your browser after changing settings.</li>
          <li>
            If you are using Citrix, VMware, Remote Desktop, or a managed
            corporate device, hardware acceleration may be disabled by policy.
            Contact your IT administrator if the issue persists.
          </li>
        </ul>
      </Alert>
    </Box>
  );
}

function MapSupportCheckProvider({ children }) {
  const [status, setStatus] = useState({
    checking: true,
    supported: false,
    error: null,
  });

  const onError = useCallback(nextStatus => {
    setStatus(nextStatus);
  }, []);

  useEffect(() => {
    try {
      const supported = isWebGLSupported();

      if (!supported) {
        setStatus({
          checking: false,
          supported: false,
          error:
            'Your browser does not support the WebGL features required to display this map.',
        });
        return;
      }

      setStatus({
        checking: false,
        supported: true,
        error: null,
      });
    } catch (err) {
      setStatus({
        checking: false,
        supported: false,
        error: err?.message || 'Unable to initialize map support check.',
      });
    }
  }, []);

  const value = useMemo(() => ({ status, onError }), [status, onError]);

  if (status.checking) {
    return (
      <MapSupportCheckContext.Provider value={value}>
        <Box p={2}>
          <Typography>Checking map support...</Typography>
        </Box>
      </MapSupportCheckContext.Provider>
    );
  }

  if (!status.supported) {
    return (
      <MapSupportCheckContext.Provider value={value}>
        <MapSupportCheckUnsupported error={status.error} />
      </MapSupportCheckContext.Provider>
    );
  }

  return (
    <MapSupportCheckContext.Provider value={value}>
      {children}
    </MapSupportCheckContext.Provider>
  );
}

export default MapSupportCheckProvider;
